// The enums of the procurement domain that travel in the contracts: @nexxt/db builds the Postgres enums from these
// tuples.

/**
 * `procurement.po_status` (spec v0.2 §5.3): generated → approved → published → in_validation → confirmed → closed.
 * From approved, published and in_validation a PO goes back to generated with a revision; before confirmed it can be
 * cancelled.
 */
export const poStatuses = [
  "generated",
  "approved",
  "published",
  "in_validation",
  "confirmed",
  "closed",
  "cancelled",
] as const;
export type PoStatus = (typeof poStatuses)[number];

/**
 * `procurement.po_line_status`. `closed`: nothing more is expected, whatever arrived; `cancelled`: never ordered
 * (I-02).
 */
export const poLineStatuses = ["open", "closed", "cancelled"] as const;
export type PoLineStatus = (typeof poLineStatuses)[number];

/** `procurement.proposal_type`: what a supplier may ask of a line of a published PO (FR-M5-05). Never more units. */
export const poChangeTypes = ["sku_swap", "qty_reduction", "line_cancellation"] as const;
export type PoChangeType = (typeof poChangeTypes)[number];

/** `procurement.proposal_status`. `withdrawn`: taken back by who proposed it, before a decision. */
export const poChangeStatuses = ["proposed", "accepted", "rejected", "withdrawn"] as const;
export type PoChangeStatus = (typeof poChangeStatuses)[number];

/**
 * `procurement.inbound_status` (spec v0.2 §5.3, with the three states of today's portal kept by B-04, ADR-128):
 * draft → documents_uploaded, or pending_authorization → authorized when a line is out of tolerance, → lines_captured
 * → three_way_passed | in_exception → ddt_generated → in_transit → partially_received → received → closed. Cancelled
 * before the DDT.
 */
export const inboundStatuses = [
  "draft",
  "documents_uploaded",
  "pending_authorization",
  "authorized",
  "lines_captured",
  "in_exception",
  "three_way_passed",
  "ddt_generated",
  "in_transit",
  "partially_received",
  "received",
  "closed",
  "cancelled",
] as const;
export type InboundStatus = (typeof inboundStatuses)[number];

/**
 * Who writes the tracking of an inbound (B-51, ADR-212): the supplier, when it arranges the carriage to the warehouse,
 * or Tomorrow, when its forwarder does.
 */
export const inboundTrackingSides = ["supplier", "tomorrow"] as const;
export type InboundTrackingSide = (typeof inboundTrackingSides)[number];

/**
 * `procurement.document_kind`: what a file linked to an inbound is. A credit note of the supplier is a
 * `credit_note_brand` (ADR-140); `credit_note_3pl` is not written in F1.
 */
export const inboundDocumentKinds = [
  "packing_list",
  "supplier_invoice",
  "waybill",
  "credit_note_brand",
  "credit_note_3pl",
  "photo",
  "other",
] as const;
export type InboundDocumentKind = (typeof inboundDocumentKinds)[number];

/** `procurement.pl_line_source`: a line of the packing list typed by Logistics, or read by the extraction tool (F2). */
export const packingListSources = ["manual", "ai_extraction"] as const;
export type PackingListSource = (typeof packingListSources)[number];

/**
 * `procurement.extraction_status` (B-50, ADR-233), the reading of the packing list of an inbound by the extraction
 * tool: `requested` (I-35 sent, the lines not back yet); `captured` (every line sure enough and the invoice adds up:
 * the lines are the packing list, and the inbound waits for its check); `to_review` (a line below the threshold, or
 * an invoice that does not add up: the lines wait for Logistics, who confirms or corrects them); `reviewed` (Logistics
 * confirmed or typed them); `failed` (the tool could not read the files, or the request never reached it: Logistics
 * types the lines, or asks again). Null on an inbound never read.
 */
export const extractionStatuses = ["requested", "captured", "to_review", "reviewed", "failed"] as const;
export type ExtractionStatus = (typeof extractionStatuses)[number];

/**
 * `procurement.three_way_result` (FR-M11-05…07, ADR-131): the check of PO, packing list and invoice `passed` or
 * `failed`; `overridden` is a failed one forced through by Operations, with its reason.
 */
export const threeWayResults = ["passed", "failed", "overridden"] as const;
export type ThreeWayResult = (typeof threeWayResults)[number];

/**
 * `procurement.receipt_waiting_reason` (I-11 §5, ADR-138): why a receipt the warehouse sent waits instead of being
 * applied, whole. No inbound has the number of the ASN; the inbound is not one the warehouse receives (not yet sent to
 * it, or already received); the warehouse is not the destination of the inbound, or has no mapping; an item none of
 * whose codes is an SKU; a line of the ASN the inbound does not have, or that carries another SKU.
 */
export const receiptWaitingReasons = [
  "unknown_inbound",
  "inbound_not_open",
  "location_mismatch",
  "unknown_item",
  "item_not_in_inbound",
] as const;
export type ReceiptWaitingReason = (typeof receiptWaitingReasons)[number];

/**
 * `procurement.receipt_outcome` (FR-M11-11, ADR-139): what the warehouse received of an inbound against what it
 * declared, once it expects nothing more, each line within its tolerance or not. `no_discrepancy` closes the inbound;
 * the others wait for Logistics, which confirms them.
 */
export const receiptOutcomes = ["no_discrepancy", "excess", "shortage", "excess_and_shortage"] as const;
export type ReceiptOutcome = (typeof receiptOutcomes)[number];

/**
 * `procurement.missing_items_resolution` (ADR-140): what the supplier does about the units missing from a received
 * inbound, once: a credit note, a later shipment (a new inbound it announces), or both.
 */
export const missingItemsResolutions = ["credit_note", "ship_later", "credit_note_and_ship_later"] as const;
export type MissingItemsResolution = (typeof missingItemsResolutions)[number];

/**
 * `procurement.credit_note_status` (ADR-140): a credit note the supplier owes for the missing units, `requested` until
 * Finance receives it or rejects it; a new file after a rejection asks again.
 */
export const creditNoteStatuses = ["requested", "received", "rejected"] as const;
export type CreditNoteStatus = (typeof creditNoteStatuses)[number];

/**
 * `procurement.demand_run_status` (ADR-143): a run creates its POs in the same request, `converted` with POs and
 * `computed` with nothing to order. `draft` and `cancelled` are in the type, not written.
 */
export const demandRunStatuses = ["draft", "computed", "converted", "cancelled"] as const;
export type DemandRunStatus = (typeof demandRunStatuses)[number];

/**
 * Why demand stays out of a run (ADR-143): the order has no warehouse yet, the brand no supplier, the supplier no
 * currency, or the SKU no purchase price of Tomorrow in it. The next run takes it, once what is missing is there.
 * `too_many_units`: it would take its PO line beyond the units one line of a message may carry (ADR-144).
 */
export const demandSkipReasons = [
  "no_warehouse",
  "no_supplier",
  "no_currency",
  "no_purchase_price",
  "too_many_units",
] as const;
export type DemandSkipReason = (typeof demandSkipReasons)[number];
